import clsx from "clsx";
import { DettaglioCassaforteRitiri, EsitoRitiro, EtichettaRitiro, PuntoRitiro } from "../../api/ritiri";
import { BarraRitiri } from "../../components/ritiri/TestataRitiri";
import { AiutoRitiri } from "../../components/ritiri/AiutoRitiri";
import { ETICHETTE, testoEsito } from "./testiLegenda";

/**
 * Ritiri valori — come si MOSTRANO i risultati del motore (§ 7): colori degli
 * esiti, badge, chip e barre con i loro aiuti, formati di date e numeri. I
 * testi delle spiegazioni stanno in `testiLegenda.ts`. Qui non si
 * decide niente: esiti, etichette, percentuali e scadenze arrivano già
 * calcolati dalle API (§ 0.6).
 */

/** Colori del badge per esito: gruppo `ritiro` di `tailwind.config.ts`. */
const COLORI_ESITO: Record<EsitoRitiro, string> = {
  rischio_blocco: "bg-ritiro-blocco text-white",
  richiedi: "bg-ritiro-richiedi text-white",
  da_ricontrollare: "bg-ritiro-osserva text-ritiro-osserva-testo",
  consigliato: "bg-ritiro-consigliato text-white",
  richiesta_inviata: "bg-ritiro-attesa text-primary",
  ritiro_fisso: "bg-ritiro-fisso text-white",
  nessuna_azione: "bg-ritiro-ok text-ritiro-ok-testo",
  nessun_dato_recente: "bg-ritiro-inattiva text-ritiro-inattiva-testo",
  esclusa: "bg-ritiro-dismessa text-ritiro-dismessa-testo",
  dismessa: "bg-ritiro-dismessa text-ritiro-dismessa-testo",
};

/**
 * Badge dell'esito; pulsa solo "Rischio blocco" (§ 7). Il testo è `esito_testo`
 * del backend. Con `conAiuto` il badge è un pulsante che apre "cosa significa ·
 * cosa fare".
 */
export function BadgeEsito({ esito, testo, className, conAiuto }: { esito: EsitoRitiro; testo: string; className?: string; conAiuto?: boolean }) {
  const badge = (
    <span
      className={clsx(
        "inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-bold",
        COLORI_ESITO[esito],
        esito === "rischio_blocco" && "ritiri-pulse",
        className
      )}
    >
      {testo}
    </span>
  );
  if (!conAiuto) return badge;
  const t = testoEsito(esito);
  return (
    <AiutoRitiri
      testo={
        <>
          {t.quando} · <strong>Cosa fare:</strong> {t.cosaFare}
        </>
      }
    >
      {badge}
    </AiutoRitiri>
  );
}

/**
 * Chip delle etichette con il nome breve del § 4.2 (da `testiLegenda.ts`). Con
 * `conAiuto` ogni chip è un pulsante che apre il testo completo del backend,
 * con numeri e date; senza (dentro un altro pulsante) resta un'etichetta.
 */
export function ChipEtichette({ etichette, conAiuto }: { etichette: EtichettaRitiro[]; conAiuto?: boolean }) {
  if (etichette.length === 0) return null;
  const chip = (e: EtichettaRitiro) => (
    <span className="rounded-full border border-ritiri-bordo-forte bg-ritiri-th-fondo px-2 py-0.5 text-[11px] font-semibold text-ritiri-th-testo">
      {ETICHETTE[e.codice]?.titolo ?? e.codice}
    </span>
  );
  return (
    <span className="flex flex-wrap gap-1.5">
      {etichette.map((e) =>
        conAiuto ? (
          <AiutoRitiri key={e.codice} testo={e.testo}>
            {chip(e)}
          </AiutoRitiri>
        ) : (
          <span key={e.codice}>{chip(e)}</span>
        )
      )}
    </span>
  );
}

// ----------------------------------------------------------------------------
// Formati. Le date arrivano come testo in ora italiana (`YYYY-MM-DD` o
// `YYYY-MM-DD HH:MM:SS`) e si leggono dai pezzi della stringa, senza fusi.
// ----------------------------------------------------------------------------

const GIORNI = ["dom", "lun", "mar", "mer", "gio", "ven", "sab"];
const GIORNI_ESTESI = ["", "lunedì", "martedì", "mercoledì", "giovedì", "venerdì", "sabato", "domenica"];

/** `2026-09-25` → "ven 25/09". */
export function giornoBreve(valore: string | null | undefined): string {
  const m = valore ? /^(\d{4})-(\d{2})-(\d{2})/.exec(valore) : null;
  if (!m) return "—";
  const settimana = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).getUTCDay();
  return `${GIORNI[settimana]} ${m[3]}/${m[2]}`;
}

/** 1 = lunedì … 7 = domenica. */
export function nomeGiorno(n: number | null): string {
  return n && GIORNI_ESTESI[n] ? GIORNI_ESTESI[n] : "—";
}

/** Quota (1 = 100%) come percentuale intera. */
export function percento(quota: number | null | undefined): string {
  return quota === null || quota === undefined ? "—" : `${Math.round(quota * 100)}%`;
}

export function euro(valore: number | null | undefined): string {
  return valore === null || valore === undefined
    ? "—"
    : valore.toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
}

export function numero(valore: number | null | undefined, cifre = 0): string {
  return valore === null || valore === undefined
    ? "—"
    : valore.toLocaleString("it-IT", { minimumFractionDigits: cifre, maximumFractionDigits: cifre });
}

/** Tono della barra: rosso dal 100%, oro dall'85% (le due linee del grafico). */
function tono(quota: number): "primary" | "accent" | "caldo" {
  return quota >= 1 ? "caldo" : quota >= 0.85 ? "accent" : "primary";
}

/**
 * Intero col punto delle migliaia anche a quattro cifre ("2.950"), come nei
 * testi del motore: `toLocaleString("it-IT")` scrive "2950".
 */
function migliaia(v: number): string {
  return Math.round(v)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

/** "1 banconota", "2.950 banconote": il numero arrotondato decide il plurale. */
function banconote(pezzi: number): string {
  return `${migliaia(pezzi)} ${Math.round(pezzi) === 1 ? "banconota" : "banconote"}`;
}

function euroMigliaia(v: number): string {
  return `${migliaia(v)} €`;
}

/** Limiti del dettaglio (`dati.limiti`): `euro` è già il 90% di `valore_assicurato`. */
export type LimitiRitiro = DettaglioCassaforteRitiri["dati"]["limiti"];

/**
 * Barra di riempimento con etichetta e percentuale; "—" senza limite o senza
 * previsione. Con `aiuto` compare un "?" accanto all'etichetta (non dentro un
 * altro pulsante: nell'elenco le barre stanno nel pulsante della riga).
 *
 * Sotto la barra, euro e banconote del punto; con `stima` (la previsione a P2)
 * preceduti da "≈". Con `limiti` (il dettaglio) anche i due limiti e quale dei
 * due fa la percentuale, che arriva già decisa dal motore (`punto.limite`): qui
 * non si confronta niente. Un limite mancante si dice, non si inventa.
 */
export function BarraRiempimento({
  etichetta,
  punto,
  aiuto,
  stima,
  limiti,
}: {
  etichetta: string;
  punto: PuntoRitiro | null;
  aiuto?: { testo: string; nome: string };
  stima?: boolean;
  limiti?: LimitiRitiro;
}) {
  const quota = punto?.percentuale ?? null;
  const circa = stima ? "≈ " : "";
  return (
    <span className={clsx("grid items-center gap-x-2 gap-y-0.5 text-xs", aiuto ? "grid-cols-[6.5rem_minmax(0,1fr)_2.75rem]" : "grid-cols-[4.5rem_minmax(0,1fr)_2.75rem]")}>
      <span className="inline-flex items-center gap-1 text-ritiri-th-testo">
        {etichetta}
        {aiuto && <AiutoRitiri testo={aiuto.testo} etichetta={`Aiuto: ${aiuto.nome}`} />}
      </span>
      {quota === null ? (
        <span className="block h-2 w-full rounded-full bg-ritiri-traccia" aria-hidden="true" />
      ) : (
        <BarraRitiri valore={quota} massimo={1} tono={tono(quota)} />
      )}
      <span className={clsx("text-right font-plex font-semibold", quota !== null && quota >= 1 ? "text-ritiri-caldo" : "text-ink")}>
        {percento(quota)}
      </span>
      {punto &&
        (limiti ? (
          <span className="col-span-2 col-start-2 block space-y-0.5 font-plex text-[11px] leading-snug text-ritiri-th-testo">
            <span className={clsx("block", punto.limite === "valore" && "font-semibold text-ink")}>
              {circa}
              {euroMigliaia(punto.euro)}
              {/* `!= null`: un campo assente (undefined) è mancante come null, mai "NaN €". */}
              {limiti.euro != null && limiti.valore_assicurato != null
                ? ` su ${euroMigliaia(limiti.euro)} (90% di ${euroMigliaia(limiti.valore_assicurato)})`
                : " · massimale non impostato"}
            </span>
            <span className={clsx("block", punto.limite === "pezzi" && "font-semibold text-ink")}>
              {circa}
              {limiti.pezzi != null ? `${banconote(punto.pezzi)} su ${migliaia(limiti.pezzi)}` : `${banconote(punto.pezzi)} · capienza non impostata`}
            </span>
            {punto.limite && (
              <span className="block font-sans">limite: {punto.limite === "pezzi" ? "banconote" : "valore"}</span>
            )}
          </span>
        ) : (
          <span className="col-span-2 col-start-2 block font-plex text-[11px] text-ritiri-th-testo">
            {circa}
            {euroMigliaia(punto.euro)} · {banconote(punto.pezzi)}
          </span>
        ))}
    </span>
  );
}
